import type { ReactNode } from 'react';
import { Button } from '@astryxdesign/core/Button';
import { Center } from '@astryxdesign/core/Center';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { Skeleton } from '@astryxdesign/core/Skeleton';
import { Spinner } from '@astryxdesign/core/Spinner';
import { VStack } from '@astryxdesign/core/VStack';
import { WarningCircleIcon } from '@phosphor-icons/react';
import { errorMessage } from '@/lib/errors';

export function FullPageLoading({ label = 'Загрузка…' }: { label?: string }) {
  return (
    <Center minHeight="100dvh" padding={6}>
      <Spinner size="lg" label={label} />
    </Center>
  );
}

export function FullPageError({ title, description, error, onRetry }: { title: string; description?: string; error?: unknown; onRetry?: () => void }) {
  return (
    <Center minHeight="100dvh" padding={6}>
      <ErrorState title={title} description={description} error={error} onRetry={onRetry} />
    </Center>
  );
}

/** Network error with a real retry action. */
export function ErrorState({ title = 'Не удалось загрузить', description, error, onRetry }: { title?: string; description?: string; error?: unknown; onRetry?: () => void }) {
  return (
    <EmptyState
      title={title}
      description={description ?? (error ? errorMessage(error) : undefined)}
      icon={<WarningCircleIcon size={32} weight="duotone" />}
      actions={onRetry ? <Button label="Повторить" variant="secondary" onClick={() => onRetry()} /> : undefined}
    />
  );
}

export function EmptyView({ title, description, action, icon }: { title: string; description?: string; action?: ReactNode; icon?: ReactNode }) {
  return <EmptyState title={title} description={description} actions={action} icon={icon} isCompact />;
}

/** Row skeletons matching list density while data loads. */
export function ListSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <VStack gap={2} aria-busy="true" aria-label="Загрузка">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} index={i} height={56} width="100%" radius={2} />
      ))}
    </VStack>
  );
}
